<template>
    <v-container class="py-7">
        <v-row>
            <v-col cols="12">
                <div class="title-container">
                    <div class="title-block">
                        <span class="page-title">QGC</span>

                        <v-breadcrumbs class="app-breadcrumb" divider="›" :items="breadcrumbItems" />
                    </div>

                    <div class="button-group">
                        <v-btn color="primary" outlined elevation="0" @click="openExportDialog">
                            Exportar QGC
                        </v-btn>
                        <v-btn color="primary" elevation="0" to="/qgc/novo">
                            Incluir
                        </v-btn>
                    </div>
                </div>
            </v-col>
        </v-row>

        <v-row>
            <v-col cols="12">
                <v-tabs v-model="selectedTabIndex" @change="changeVersion">
                    <v-tab v-for="tab in versionTabs" :key="tab.id">
                        {{ tab.label }}
                    </v-tab>
                </v-tabs>
            </v-col>
        </v-row>

        <v-row>
            <v-col cols="12">
                <v-card class="filter-card">
                    <v-card-title class="filter-card-title">Filtro</v-card-title>
                    <v-card-text>
                        <v-form @submit.prevent="applyFilters">
                            <v-row>
                                <v-col cols="12" md="4">
                                    <v-text-field v-model="filters.name" label="Nome" outlined dense hide-details clearable />
                                </v-col>
                                <v-col cols="12" md="2">
                                    <v-text-field v-model="filters.registration" label="CPF/CNPJ" outlined dense hide-details clearable />
                                </v-col>
                                <v-col cols="12" md="3">
                                    <v-text-field v-model="filters.email" label="E-mail" outlined dense hide-details clearable />
                                </v-col>
                                <v-col cols="12" md="3">
                                    <v-autocomplete v-model="filters.client" label="Caso" :items="clientList" item-text="name"
                                        item-value="id" outlined dense hide-details clearable no-data-text="Nenhum caso encontrado" />
                                </v-col>
                                <v-col cols="12" md="4">
                                    <v-select v-model="filters.type" label="Classe" :items="dataset.claimClasses"
                                        item-text="name" item-value="id" outlined dense hide-details clearable />
                                </v-col>
                                <v-col cols="12" md="2" class="d-flex align-center">
                                    <v-btn type="submit" color="primary" elevation="0" block>Filtrar</v-btn>
                                </v-col>
                            </v-row>
                        </v-form>
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>

        <v-row>
            <v-col cols="12">
                <v-card class="table-card">
                    <v-card-text>
                        <v-data-table :headers="headers" :items="items" :loading="loading" :options.sync="tableOptions"
                            :server-items-length="totalItems" :footer-props="footerProps"
                            no-data-text="Nenhum registro encontrado" loading-text="Carregando dados..."
                            class="app-table hover-table">
                            <template v-slot:[`item.class`]="{ item }">
                                <span>{{ item.class | enum(dataset.claimClasses, 'id', 'name') }}</span>
                            </template>

                            <template v-slot:[`item.cpf`]="{ item }">
                                <v-tooltip v-if="!item.hasValidDocument" bottom>
                                    <template #activator="{ on }">
                                        <v-chip v-on="on" x-small color="warning" text-color="white">
                                            Pendente
                                        </v-chip>
                                    </template>
                                    <span>CPF/CNPJ não informado. Nome usado como identificador temporário.</span>
                                </v-tooltip>
                                <span v-else>{{ item.cpf }}</span>
                            </template>

                            <template v-slot:[`item.actions`]="{ item }">
                                <div class="text-no-wrap">
                                    <v-tooltip bottom>
                                        <template #activator="{ on }">
                                            <v-icon v-on="on" size="20" class="mr-2" @click="editItem(item)">
                                                mdi-pencil-outline
                                            </v-icon>
                                        </template>
                                        <span>Editar</span>
                                    </v-tooltip>

                                    <v-tooltip bottom>
                                        <template #activator="{ on }">
                                            <v-icon v-on="on" size="20" @click="deleteItem(item)">
                                                mdi-delete-outline
                                            </v-icon>
                                        </template>
                                        <span>Excluir</span>
                                    </v-tooltip>
                                </div>
                            </template>
                        </v-data-table>
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>

        <!-- DIALOG EXCLUIR -->
        <v-dialog v-model="deleteDialog" max-width="400px">
            <v-card>
                <v-card-title>
                    <v-icon large class="mr-2 error--text">
                        mdi-alert-circle-outline
                    </v-icon>
                    Aviso
                </v-card-title>

                <v-card-text class="pt-4 black--text">
                    Tem certeza que deseja excluir o registro
                    <strong v-if="itemToDelete">
                        {{ itemToDelete.name }}
                    </strong>?
                </v-card-text>

                <v-card-actions>
                    <v-spacer />

                    <v-btn text @click="deleteDialog = false">
                        Cancelar
                    </v-btn>

                    <v-btn color="red darken-1" text @click="confirmDelete">
                        Excluir
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- DIALOG EXPORTAR QGC -->
        <v-dialog v-model="exportDialog" max-width="500px">
            <v-card>
                <v-card-title>
                    Exportar QGC
                </v-card-title>

                <v-card-text class="pt-4">
                    <v-form ref="exportForm">
                        <v-select v-model="exportFilters.version" label="Edital" :items="versionTabs"
                            item-text="label" item-value="id" outlined dense hide-details="auto"
                            :rules="[$rules.required]" class="mb-4" />

                        <v-select v-model="exportFilters.client_id" label="Caso" :items="clientList"
                            item-text="name" item-value="id" outlined dense hide-details="auto" clearable />
                    </v-form>
                </v-card-text>

                <v-card-actions>
                    <v-spacer />

                    <v-btn text @click="exportDialog = false">
                        Cancelar
                    </v-btn>

                    <v-btn color="primary" :loading="exporting" @click="confirmExport">
                        Exportar
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-container>
</template>

<script>
import axios from 'axios';
import claimClasses from '@/common/data/creditor-types.data.js';
import qgcVersions from '@/common/data/qgc-versions.data.js';

export default {
    name: "QgcList",

    data: () => ({
        loading: false,
        deleteDialog: false,
        itemToDelete: null,

        exportDialog: false,
        exporting: false,
        exportFilters: {
            version: null,
            client_id: null,
        },

        dataset: {
            claimClasses: claimClasses
        },

        filters: {
            name: null,
            email: null,
            registration: null,
            client: null,
            type: null
        },

        clientList: [],

        headers: [
            { text: "Nome", value: "name" },
            { text: "CPF/CNPJ", value: "cpf" },
            { text: "Caso", value: "client" },
            { text: "Classe", value: "class" },
            { text: "Valor", value: "value" },
            { text: "Criado em", value: "createdAt" },
            { text: "Ações", value: "actions", sortable: false }
        ],

        items: [],
        totalItems: 0,

        tableOptions: {
            page: 1,
            itemsPerPage: 10,
        },

        footerProps: {
            'items-per-page-options': [10, 20, 50, 100],
            'items-per-page-text': 'Linhas por página:',
        },

        versionTabs: qgcVersions,

        selectedTabIndex: 0,
    }),

    computed: {
        breadcrumbItems() {
            return [
                { text: "Início", to: "/home", exact: true },
                { text: "QGC", disabled: true }
            ];
        },

        currentVersion() {
            const currentTab = this.versionTabs[this.selectedTabIndex];
            return currentTab ? Number(currentTab.id) : undefined;
        }
    },

    created() {
        this.init();
    },

    watch: {
        tableOptions: {
            handler() {
                this.load();
            },
            deep: true
        }
    },

    methods: {

        async init() {
            await this.loadClients();
            this.load();
        },

        applyFilters() {
            if (this.tableOptions.page === 1) {
                this.load();
                return;
            }

            this.tableOptions = {
                ...this.tableOptions,
                page: 1
            };
        },

        changeVersion() {
            if (this.tableOptions.page === 1) {
                this.load();
                return;
            }

            this.tableOptions = {
                ...this.tableOptions,
                page: 1
            };
        },

        async loadClients() {
            try {
                const response = await axios.get('/api/clients', { params: { limit: 1000 } });
                this.clientList = (response.data.content && response.data.content.data) || [];
            } catch (error) {
                console.error("Erro ao carregar casos", error);
            }
        },

        buildSearch() {
            const normalizedVersion = this.currentVersion;
            return JSON.stringify({
                name: this.filters.name || undefined,
                registration: this.filters.registration || undefined,
                email: this.filters.email || undefined,
                client_id: this.filters.client || undefined,
                type: this.filters.type || undefined,
                version: Number.isNaN(normalizedVersion) ? undefined : normalizedVersion
            });
        },

        async load() {
            this.loading = true;

            try {
                const page = this.tableOptions.page || 1;
                const limit = this.tableOptions.itemsPerPage > 0 ? this.tableOptions.itemsPerPage : 10;
                const params = {
                    page,
                    limit,
                    search: this.buildSearch()
                };

                const response = await axios.get('/api/admin/creditor-claims', { params });

                const content = response.data.content || {};
                const rows = content.data || content.rows || [];
                this.totalItems = Number(content.total || content.totalItems || rows.length || 0);

                this.items = rows.map(r => {
                    const raw = r.registration ? String(r.registration).replace(/\D/g, '') : '';
                    const foundClient = this.clientList.find(c => c.id === r.client_id);

                    const hasValidDocument = raw.length <= 11
                        ? this.$rules.cpf(r.registration) === true
                        : this.$rules.cnpj(r.registration) === true;

                    function maskCpfCnpj(digits) {
                        if (!digits) return null;
                        if (digits.length <= 11) {
                            return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{0,2})/, function (_, a, b, c, d) {
                                return a + (b ? '.' + b : '') + (c ? '.' + c : '') + (d ? '-' + d : '');
                            });
                        }
                        return digits.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{0,2})/, function (_, a, b, c, d, e) {
                            return a + (b ? '.' + b : '') + (c ? '.' + c : '') + (d ? '/' + d : '') + (e ? '-' + e : '');
                        });
                    }

                    return {
                        id: r.id,
                        name: r.name,
                        cpf: maskCpfCnpj(raw),
                        hasValidDocument,
                        client: (r.client && r.client.name) || (foundClient && foundClient.name) || r.client_id || 'N/A',
                        class: r.type || null,
                        value: r.amount === null || r.amount === undefined ? null : Number(r.amount).toLocaleString('pt-BR', { minimumFractionDigits: 2 }),
                        createdAt: r.created_at ? new Date(r.created_at).toLocaleDateString('pt-BR') : null
                    };
                });
            } catch (error) {
                this.$dialog.notify.error(
                    this.$codes(error) || 'Erro ao carregar QGC',
                    { position: 'top-right', timeout: 5000 }
                );
            } finally {
                this.loading = false;
            }
        },

        editItem(item) {
            this.$router.push(`/qgc/${item.id}`);
        },

        deleteItem(item) {
            this.itemToDelete = item;
            this.deleteDialog = true;
        },

        async confirmDelete() {
            if (!this.itemToDelete) return;

            try {
                await axios.delete(`/api/admin/creditor-claims/${this.itemToDelete.id}`);

                this.$dialog.notify.success('Registro excluído', { position: 'top-right', timeout: 3000 });
                this.deleteDialog = false;
                this.itemToDelete = null;
                this.load();
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error) || 'Erro ao excluir', { position: 'top-right', timeout: 5000 });
            }
        },

        openExportDialog() {
            this.exportFilters = {
                version: this.currentVersion || null,
                client_id: null,
            };
            this.exportDialog = true;
        },

        async confirmExport() {
            if (!this.$refs.exportForm.validate()) return;

            this.exporting = true;
            try {
                const params = {
                    version: this.exportFilters.version,
                };
                if (this.exportFilters.client_id) {
                    params.client_id = this.exportFilters.client_id;
                }

                const response = await axios.get('/api/admin/creditor-claims/export', {
                    params,
                    responseType: 'blob',
                });

                const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = 'qgc_export.csv';
                a.click();
                URL.revokeObjectURL(url);

                this.exportDialog = false;
            } catch (error) {
                this.$dialog.notify.error('Erro ao exportar QGC', { position: 'top-right', timeout: 5000 });
            } finally {
                this.exporting = false;
            }
        }
    }
};
</script>
